import { useId, useState, type ReactElement } from 'react';

import { useToast } from '@/components/ui/toast';

import { settingsErrorCopy, stageLabel } from '../presentation';
import { parseIntIn } from '../rule-form';
import { DEADLINE_STAGES, type DeadlineStage, type StageDeadlines } from '../types';
import { useUpdateStageDeadlines } from '../use-notification-settings';

type Draft = Record<DeadlineStage, string>;

const toDraft = (d: StageDeadlines): Draft => ({
  elaboracion: String(d.elaboracion),
  revision: String(d.revision),
  aprobacion: String(d.aprobacion),
  capacitacion: String(d.capacitacion),
});

/** Días asignados a cada etapa del flujo (base de "mitad del plazo" y "vencida"). En
 *  Capacitación (G4) solo sugieren la vigencia al firmar: esa etapa vence con la vigencia.
 *  Guardar se habilita solo con cambios válidos (1 a 365 días, como el backend). */
export function StageDeadlinesCard({ deadlines }: { deadlines: StageDeadlines }): ReactElement {
  const [draft, setDraft] = useState<Draft>(() => toDraft(deadlines));
  const baseId = useId();
  const update = useUpdateStageDeadlines();
  const { showToast } = useToast();

  const parsed = DEADLINE_STAGES.map((s) => [s, parseIntIn(draft[s], 1, 365)] as const);
  const valid = parsed.every(([, n]) => n !== null);
  const dirty = parsed.some(([s, n]) => n !== deadlines[s]);

  const save = (): void => {
    if (!valid || !dirty || update.isPending) {
      return;
    }
    const body = Object.fromEntries(parsed) as StageDeadlines;
    update.mutate(body, {
      onSuccess: () => showToast('Plazos por etapa actualizados'),
      onError: (e) => showToast(settingsErrorCopy(e), 'error'),
    });
  };

  return (
    <div className="card rise d3">
      <div className="ch">
        <h3>Plazos por etapa</h3>
        <span className="count">días por etapa</span>
      </div>
      <div className="cb">
        <div className="deadline-grid">
          {DEADLINE_STAGES.map((s) => {
            const bad = parseIntIn(draft[s], 1, 365) === null;
            const id = `${baseId}-${s}`;
            // Capacitación (G4): sus días solo sugieren la vigencia; vence con ella.
            const hint = s === 'capacitacion';
            const describedBy =
              [bad ? `${id}-err` : null, hint ? `${id}-hint` : null].filter(Boolean).join(' ') ||
              undefined;
            return (
              <div key={s} className="fld">
                <label htmlFor={id} className="fld-lab">
                  {stageLabel(s)}
                </label>
                <input
                  id={id}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={365}
                  className="selector fw"
                  value={draft[s]}
                  aria-invalid={bad}
                  aria-describedby={describedBy}
                  onChange={(e) => setDraft({ ...draft, [s]: e.target.value })}
                />
                {bad && (
                  <span id={`${id}-err`} className="fld-err">
                    Entre 1 y 365 días.
                  </span>
                )}
                {hint && (
                  <span id={`${id}-hint`} className="fld-hint">
                    Sugiere la vigencia al firmar. La capacitación vence con la vigencia del
                    documento.
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <div className="deadline-actions">
          <button
            type="button"
            className="btn sm"
            disabled={!valid || !dirty || update.isPending}
            onClick={save}
          >
            {update.isPending ? 'Guardando…' : 'Guardar plazos'}
          </button>
        </div>
      </div>
    </div>
  );
}
